import { Button } from 'app/components/ui/button'
import {
  EDITABLE_FIELD_CHOICES,
  editableFieldsFor,
  EDIT_OPERATIONS,
  type Option,
  TAG_OPERATIONS,
} from 'app/lib/workflows/actionCatalogs'
import type {
  AddNoteNodeData,
  AssignOwnerNodeData,
  EditFieldChange,
  EditObjectNodeData,
  EditOperation,
  LogFollowUpNodeData,
  TagObjectNodeData,
  TagOperation,
  TargetObject,
  UpdateAppointmentNodeData,
} from 'app/types/workflows'
import { fieldsToVariables } from 'app/lib/workflows/dataContext'
import { type Panel, useChannelChoices } from './panelShared'
import { ChoiceSelect, type Choice, Field, MessageField, VariableTextField } from './fields'
import { Section } from './Section'
import { TagPicker } from './TagPicker'
import { optionChoices, useUserOptions } from './panelShared'

const TARGET_LABEL: Record<TargetObject, string> = { contact: `Contact`, activity: `Activity` }

/** Object-target choices, always including the current value so a stale target stays selectable. */
const targetChoices = (targets: TargetObject[], current: TargetObject): Choice[] =>
  (targets.includes(current) ? targets : [...targets, current]).map((t) => ({ value: t, label: TARGET_LABEL[t] }))

export function TagObjectPanel({ data, set, errors, targets }: Panel<TagObjectNodeData> & { targets: TargetObject[] }) {
  return (
    <>
      <Section title="Applies to">
        <Field label="Record">
          <ChoiceSelect
            value={data.target}
            choices={targetChoices(targets, data.target)}
            onChange={(v) => set({ target: v as TargetObject })}
          />
        </Field>
      </Section>
      <Section title="Tags">
        <Field label="Action">
          <ChoiceSelect
            value={data.operation}
            choices={TAG_OPERATIONS}
            onChange={(v) => set({ operation: v as TagOperation })}
          />
        </Field>
        <Field label="Tags" error={errors.tags}>
          <TagPicker value={data.tags} onChange={(tags) => set({ tags })} />
        </Field>
      </Section>
    </>
  )
}

export function EditObjectPanel({
  data,
  set,
  errors,
  targets,
  availableFields,
  sample,
}: Panel<EditObjectNodeData> & {
  targets: TargetObject[]
  availableFields: string[]
  sample?: Record<string, string>
}) {
  const update = (index: number, patch: Partial<EditFieldChange>) =>
    set({ fields: data.fields.map((row, i) => (i === index ? { ...row, ...patch } : row)) })
  const fieldChoices: Record<string, readonly Choice[]> = { ...EDITABLE_FIELD_CHOICES, channel: useChannelChoices() }
  return (
    <>
      <Section title="Applies to">
        <Field label="Record">
          <ChoiceSelect
            value={data.target}
            choices={targetChoices(targets, data.target)}
            // Contact and activity have disjoint editable fields, so switching target invalidates
            // every row. Clearing back to one blank row is honest about that.
            onChange={(v) => set({ target: v as TargetObject, fields: [{ operation: `set`, value: `` }] })}
          />
        </Field>
      </Section>
      <Section title="Changes">
        <Field
          label="Fields"
          hint="Each row is one field. They are all written in a single update."
          error={errors.fields}
        >
          <div className="flex flex-col gap-3">
            {data.fields.map((row, index) => (
              <div key={index} className="flex flex-col gap-1.5 rounded-md border border-default bg-surface p-2">
                <div className="flex items-center gap-1.5">
                  <ChoiceSelect
                    value={row.field ?? ``}
                    choices={[...editableFieldsFor(data.target)]}
                    placeholder="Select a field…"
                    invalid={!!errors[`fields.${index}.field`]}
                    onChange={(v) => update(index, { field: v || undefined })}
                  />
                  <ChoiceSelect
                    value={row.operation}
                    choices={EDIT_OPERATIONS}
                    onChange={(v) => update(index, { operation: v as EditOperation })}
                  />
                  <Button
                    variant="subtle"
                    size="xs"
                    aria-label={`Remove field ${index + 1}`}
                    onClick={() => set({ fields: data.fields.filter((_, i) => i !== index) })}
                    className="ml-auto"
                  >
                    Remove
                  </Button>
                </div>
                {errors[`fields.${index}.field`] && (
                  <div className="text-[11px] text-red-600">{errors[`fields.${index}.field`]}</div>
                )}
                {row.operation !== `clear` &&
                  (fieldChoices[row.field ?? ``] ? (
                    <>
                      <ChoiceSelect
                        value={row.value}
                        choices={
                          row.value && !fieldChoices[row.field ?? ``].some((c) => c.value === row.value)
                            ? [...fieldChoices[row.field ?? ``], { value: row.value, label: row.value }]
                            : fieldChoices[row.field ?? ``]
                        }
                        placeholder="Value to set"
                        ariaLabel="Value to set"
                        invalid={!!errors[`fields.${index}.value`]}
                        onChange={(value) => update(index, { value })}
                      />
                      {errors[`fields.${index}.value`] && (
                        <div className="text-[11px] text-red-600">{errors[`fields.${index}.value`]}</div>
                      )}
                    </>
                  ) : (
                    <VariableTextField
                      value={row.value}
                      error={errors[`fields.${index}.value`]}
                      onChange={(value) => update(index, { value })}
                      variables={fieldsToVariables(availableFields)}
                      sample={sample}
                      placeholder="Value to set"
                    />
                  ))}
              </div>
            ))}
            <Button
              variant="dashed"
              size="xs"
              onClick={() => set({ fields: [...data.fields, { operation: `set`, value: `` }] })}
              className="self-start"
            >
              + Add a field
            </Button>
          </div>
        </Field>
      </Section>
    </>
  )
}

export function UpdateAppointmentPanel(_props: Panel<UpdateAppointmentNodeData>) {
  return (
    <Section>
      <p className="text-sm text-subtle">
        Marks the appointment confirmed in your practice system, so the front desk sees it there. A confirmed
        appointment gets no more confirmation texts.
      </p>
    </Section>
  )
}

export function AssignOwnerPanel({ data, set, errors }: Panel<AssignOwnerNodeData>) {
  const owners = useUserOptions()
  return (
    <>
      <Section>
        <Field
          label="Owner"
          error={errors.ownerId}
          hint="Not applied by the engine yet: owner is only settable when a contact is created."
        >
          <ChoiceSelect
            value={data.ownerId ?? ``}
            choices={optionChoices(owners)}
            placeholder={owners.length ? `Select a staff member…` : `Loading…`}
            invalid={!!errors.ownerId}
            onChange={(v) => set({ ownerId: v || undefined, ownerName: owners.find((o) => o.id === v)?.name })}
          />
        </Field>
      </Section>
    </>
  )
}

export function AddNotePanel({
  data,
  set,
  errors,
  availableFields,
  sample,
}: Panel<AddNoteNodeData> & { availableFields: string[]; sample?: Record<string, string> }) {
  return (
    <>
      <Section>
        <Field label="Note" info="Logged on the contact's timeline as a note activity." error={errors.body}>
          <MessageField
            value={data.body}
            onChange={(body) => set({ body })}
            error={errors.body}
            variables={fieldsToVariables(availableFields)}
            sample={sample}
            rows={4}
            placeholder="Write the note…"
          />
        </Field>
      </Section>
    </>
  )
}

const FOLLOW_UP_DATE_CHOICES: Choice[] = [
  { value: `event`, label: `When it happened in the PMS` },
  { value: `run`, label: `When the workflow runs` },
]

const FOLLOW_UP_CONNECTED_CHOICES: Choice[] = [
  { value: `fromDirection`, label: `From direction (received = yes)` },
  { value: `yes`, label: `Yes` },
  { value: `no`, label: `No` },
]

const NO_OWNER: Option[] = [{ id: ``, name: `No owner` }]

export function LogFollowUpPanel({
  data,
  set,
  errors,
  availableFields,
  sample,
}: Panel<LogFollowUpNodeData> & { availableFields: string[]; sample?: Record<string, string> }) {
  const owners = useUserOptions({ tokens: NO_OWNER })
  return (
    <>
      <Section>
        <div className="text-xs text-subtle">
          Creates the same record as the Follow Up action on a contact. Lead status is never changed.
        </div>
        <Field label="When" error={errors.dateSource}>
          <ChoiceSelect
            value={data.dateSource}
            choices={FOLLOW_UP_DATE_CHOICES}
            invalid={!!errors.dateSource}
            onChange={(v) => set({ dateSource: v as LogFollowUpNodeData[`dateSource`] })}
          />
        </Field>
        <Field label="Connected" error={errors.connected}>
          <ChoiceSelect
            value={data.connected}
            choices={FOLLOW_UP_CONNECTED_CHOICES}
            invalid={!!errors.connected}
            onChange={(v) => set({ connected: v as LogFollowUpNodeData[`connected`] })}
          />
        </Field>
        <Field label="Note" error={errors.note}>
          <MessageField
            value={data.note}
            onChange={(note) => set({ note })}
            error={errors.note}
            variables={fieldsToVariables(availableFields)}
            sample={sample}
            rows={4}
            placeholder="Write the note…"
          />
        </Field>
        <Field label="Owner">
          <ChoiceSelect
            value={data.ownerId ?? ``}
            choices={optionChoices(owners)}
            placeholder="No owner"
            onChange={(v) => set({ ownerId: v || undefined, ownerName: owners.find((o) => v && o.id === v)?.name })}
          />
        </Field>
      </Section>
    </>
  )
}
